import {
  THREADS_SHOP,
  ITEM_NAMES,
  ITEM_DESCRIPTIONS,
  type ShopEntry,
} from "@ghostland/shared";
import { balanceStore } from "../items/balanceStore.js";
import { inventoryStore } from "../items/inventoryStore.js";
import { itemThumb } from "../items/itemThumb.js";
import { injectTheme } from "./theme.js";
import { injectBillStyle, cashIcon } from "./pixelBills.js";

/**
 * The THREADS clothing-store shop. A clean dark panel listing the stock: a colour swatch,
 * name + description, price (gold coin), and a Buy button. The Ghost Tee shows "Not for sale".
 * Buying goes through the server (cash debit + item granted); the panel re-renders on balance
 * changes so affordability + "owned" stay live.
 */
const STYLE_ID = "ghostland-shop-style";

const CSS = `
#gl-shop-overlay {
  position: fixed; inset: 0; z-index: 33; display: none;
  align-items: center; justify-content: center;
  background: rgba(0,0,0,.72); color: var(--gl-text); font-family: var(--gl-font);
}
#gl-shop-overlay.open { display: flex; }
#gl-shop-card {
  width: min(460px, 94vw); max-height: 90vh; display: flex; flex-direction: column;
  background: var(--gl-panel); border: 1px solid var(--gl-border); border-radius: 12px;
  box-shadow: 0 24px 64px rgba(0,0,0,.7); overflow: hidden;
}
#gl-shop-head {
  display: flex; align-items: baseline; justify-content: space-between;
  padding: 16px 18px 12px; border-bottom: 1px solid var(--gl-border);
}
#gl-shop-head h2 { margin: 0; font-size: 17px; letter-spacing: 2px; }
#gl-shop-bal { font-size: 13px; color: var(--gl-muted); display: inline-flex; align-items: center; gap: 5px; }
#gl-shop-bal b { color: #ffe27a; }
#gl-shop-list { overflow-y: auto; padding: 8px; display: flex; flex-direction: column; gap: 6px; }
.gl-shop-item {
  display: flex; align-items: center; gap: 12px; padding: 9px 10px;
  background: var(--gl-panel-2); border: 1px solid var(--gl-border); border-radius: 8px;
}
.gl-shop-thumb {
  width: 42px; height: 42px; flex: none; border-radius: 6px; background: var(--gl-panel);
  border: 1px solid var(--gl-border); display: flex; align-items: center; justify-content: center;
}
.gl-shop-thumb canvas { width: 38px; height: 38px; image-rendering: pixelated; }
.gl-shop-info { flex: 1; min-width: 0; }
.gl-shop-name { font-size: 13px; font-weight: 700; }
.gl-shop-desc { font-size: 11px; color: var(--gl-muted); margin-top: 1px; }
.gl-shop-price { font-size: 13px; font-weight: 700; color: #ffe27a; display: inline-flex; align-items: center; gap: 4px; }
.gl-shop-buy {
  flex: none; min-width: 78px; padding: 8px 12px; cursor: pointer; border-radius: 6px;
  border: none; background: var(--gl-accent); color: var(--gl-accent-fg); font-size: 12px; font-weight: 700;
}
.gl-shop-buy:hover:not(:disabled) { background: var(--gl-accent-hover); }
.gl-shop-buy:disabled { opacity: .4; cursor: default; }
.gl-shop-tag { flex: none; min-width: 78px; text-align: center; font-size: 11px; color: var(--gl-muted); }
.gl-shop-tag.owned { color: #79d18a; }
.gl-shop-right { display: flex; flex-direction: column; align-items: flex-end; gap: 5px; }
#gl-shop-foot { padding: 10px 18px 14px; border-top: 1px solid var(--gl-border); text-align: right; }
#gl-shop-close { padding: 9px 18px; cursor: pointer; border-radius: 6px; border: 1px solid var(--gl-border); background: var(--gl-panel-2); color: var(--gl-text); font-size: 12px; }
#gl-shop-close:hover { border-color: var(--gl-border-focus); }
`;

export interface ShopActions {
  buy?(itemId: string): void;
}
let actions: ShopActions = {};
export function setShopActions(a: ShopActions): void {
  actions = a;
}

/** A shop to display: a title + its stock. */
export interface ShopConfig { title: string; entries: ReadonlyArray<ShopEntry>; }
const DEFAULT_SHOP: ShopConfig = { title: "THREADS", entries: THREADS_SHOP };

let overlay: HTMLDivElement | null = null;
let titleEl: HTMLElement | null = null;
let listEl: HTMLDivElement | null = null;
let balEl: HTMLElement | null = null;
let unsub: (() => void) | null = null;
let current: ShopConfig = DEFAULT_SHOP;

function ensureStyle(): void {
  injectTheme();
  injectBillStyle();
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = CSS;
  document.head.appendChild(s);
}

function build(): HTMLDivElement {
  ensureStyle();
  const el = document.createElement("div");
  el.id = "gl-shop-overlay";
  el.innerHTML = `
    <div id="gl-shop-card">
      <div id="gl-shop-head">
        <h2 id="gl-shop-title">THREADS</h2>
        <span id="gl-shop-bal">Cash: ${cashIcon()} <b id="gl-shop-bal-amt">0</b></span>
      </div>
      <div id="gl-shop-list"></div>
      <div id="gl-shop-foot"><button id="gl-shop-close" type="button">Close</button></div>
    </div>`;
  el.addEventListener("click", (e) => { if (e.target === el) closeShop(); });
  el.querySelector("#gl-shop-close")!.addEventListener("click", () => closeShop());
  titleEl = el.querySelector<HTMLElement>("#gl-shop-title");
  listEl = el.querySelector<HTMLDivElement>("#gl-shop-list");
  balEl = el.querySelector<HTMLElement>("#gl-shop-bal-amt");
  document.body.appendChild(el);
  return el;
}

/** Rebuild the list for the currently-selected shop. */
function renderList(): void {
  if (!listEl) return;
  if (titleEl) titleEl.textContent = current.title;
  listEl.innerHTML = "";
  for (const entry of current.entries) {
    const row = document.createElement("div");
    row.className = "gl-shop-item";
    row.dataset.item = entry.itemId;
    row.innerHTML = `
      <span class="gl-shop-thumb"></span>
      <div class="gl-shop-info">
        <div class="gl-shop-name">${ITEM_NAMES[entry.itemId]}${(entry.qty ?? 1) > 1 ? ` \u00d7${entry.qty}` : ""}</div>
        <div class="gl-shop-desc">${ITEM_DESCRIPTIONS[entry.itemId]}</div>
      </div>
      <div class="gl-shop-right">
        ${entry.forSale ? `<span class="gl-shop-price">${cashIcon()} ${entry.price.toLocaleString()}</span>` : ""}
        ${entry.forSale
          ? `<button class="gl-shop-buy" type="button">Buy</button>`
          : `<span class="gl-shop-tag">Not for sale</span>`}
      </div>`;
    row.querySelector(".gl-shop-thumb")!.appendChild(itemThumb(entry.itemId));
    if (entry.forSale) {
      row.querySelector<HTMLButtonElement>(".gl-shop-buy")!
        .addEventListener("click", () => actions.buy?.(entry.itemId));
    }
    listEl.appendChild(row);
  }
}

/** Refresh balance display + per-row affordability / owned state. */
function refresh(balance: number): void {
  if (!overlay) return;
  if (balEl) balEl.textContent = balance.toLocaleString();
  const owned = new Set(inventoryStore.get().map((s) => s.itemId));
  for (const entry of current.entries) {
    const row = overlay.querySelector<HTMLDivElement>(`.gl-shop-item[data-item="${entry.itemId}"]`);
    if (!row) continue;
    const buy = row.querySelector<HTMLButtonElement>(".gl-shop-buy");
    if (!buy) continue;
    const has = owned.has(entry.itemId);
    buy.disabled = balance < entry.price;
    buy.textContent = has ? "Buy again" : "Buy";
  }
}

export function showShop(shop?: ShopConfig): void {
  current = shop ?? DEFAULT_SHOP;
  if (!overlay) overlay = build();
  renderList();
  overlay.classList.add("open");
  unsub?.();
  unsub = balanceStore.subscribe(refresh); // fires immediately with the current balance
}

export function closeShop(): void {
  overlay?.classList.remove("open");
  unsub?.(); unsub = null;
}

export function isShopOpen(): boolean {
  return !!overlay?.classList.contains("open");
}
